import { Controller } from "@hotwired/stimulus";
import { refreshCsrfToken } from "../../../helpers/csrf";
import { setUploadBlocking } from "../../../helpers/upload_submit_lock";
import "./component.scss";

export default class extends Controller {
  static targets = ["preview", "changeButton", "dropzone"];

  connect() {}

  changeFile(event) {
    event.preventDefault();
    // One upload per field at a time, so a late failure cannot restore an outdated value.
    if (this.uploading) return;

    this.openFileDialog();
  }

  openFileDialog() {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    
    // Use component's accepted_files or default to images
    const acceptedFiles = this.data.get("acceptedFiles") || "image/jpeg,image/jpg,image/png,image/webp";
    fileInput.accept = acceptedFiles;
    fileInput.style.display = "none";

    fileInput.addEventListener("change", (e) => {
      const file = e.target.files[0];
      if (file) {
        this.replaceExistingFile(file);
      }
      document.body.removeChild(fileInput);
    });

    document.body.appendChild(fileInput);
    fileInput.click();
  }

  replaceExistingFile(file) {
    // Update visual preview immediately
    this.updatePreview(file);
    // Upload file and prepare for form submission
    this.uploadNewFile(file);
  }

  updatePreview(file) {
    if (!this.hasPreviewTarget) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      if (this.isImage(file)) {
        this.updateImagePreview(e.target.result);
      } else {
        this.updateFilePreview(file);
      }
      this.ensurePreviewVisible();
    };
    reader.readAsDataURL(file);
  }

  updateImagePreview(imageSrc) {
    const imageContainer = this.previewTarget.querySelector(
      ".c-form--file_field_replaceable-preview__image"
    );
    if (imageContainer) {
      this.replaceImageElement(imageContainer, imageSrc);
    }
  }

  updateFilePreview(file) {
    const fileNameElement = this.previewTarget.querySelector(
      ".c-form--file_field_replaceable-preview-file__name"
    );
    if (fileNameElement) {
      const textNode = fileNameElement.childNodes[1]; // Text node after SVG
      if (textNode) {
        textNode.textContent = this.formatFileName(file.name);
      }
    }
  }

  replaceImageElement(container, imageSrc) {
    // Get original dimensions before replacing
    const pictureElement = container.querySelector("picture");
    const originalImg = pictureElement?.querySelector("img");
    let originalHeight = "8rem"; // default fallback

    if (originalImg) {
      const computedStyle = window.getComputedStyle(originalImg);
      originalHeight = computedStyle.height;
    }

    // Ensure container maintains its size
    container.style.height = originalHeight;
    container.style.minHeight = originalHeight;
    container.style.maxHeight = originalHeight;

    const newImg = document.createElement("img");
    newImg.src = imageSrc;
    newImg.alt = "";
    newImg.loading = "lazy";

    if (originalImg) {
      // Copy CSS classes from original
      newImg.className = originalImg.className;
    }

    // Force the new image to fit the container
    newImg.style.cssText = `
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      max-width: 100%;
      max-height: 100%;
    `;

    if (pictureElement) {
      pictureElement.replaceWith(newImg);
    }
  }

  ensurePreviewVisible() {
    this.previewTarget.dataset.attached = "true";
    this.previewTarget.style.display = "block";
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.style.display = "none";
    }
  }

  uploadNewFile(file) {
    const formParamName = this.previewTarget.dataset.formParamName;
    const previousInputs = this.clearExistingInputs(formParamName);
    this.uploadFileDirectly(file, formParamName, previousInputs);
  }

  clearExistingInputs(paramName) {
    const existingInputs = Array.from(
      this.element.querySelectorAll(`input[name="${paramName}"]`)
    );
    existingInputs.forEach((input) => input.remove());
    return existingInputs;
  }

  async uploadFileDirectly(file, paramName, previousInputs = []) {
    this.setUploading(true);

    try {
      await refreshCsrfToken();
      const { DirectUpload } = await import("@rails/activestorage");
      const upload = new DirectUpload(
        file,
        "/rails/active_storage/direct_uploads"
      );

      upload.create((error, blob) => {
        if (error) {
          console.error("File upload failed:", error);
          this.restoreInputs(previousInputs);
        } else {
          this.createHiddenInput(paramName, blob.signed_id);
        }
        this.setUploading(false);
      });
    } catch (error) {
      console.error("Failed to load ActiveStorage:", error);
      this.restoreInputs(previousInputs);
      this.setUploading(false);
    }
  }

  setUploading(uploading) {
    this.uploading = uploading;
    if (this.hasChangeButtonTarget) this.changeButtonTarget.disabled = uploading;
    setUploadBlocking(this.element, uploading);
  }

  // A failed upload keeps the previous file instead of silently dropping it.
  restoreInputs(inputs) {
    inputs.forEach((input) => this.element.appendChild(input));
  }

  createHiddenInput(name, value) {
    const hiddenInput = document.createElement("input");
    hiddenInput.type = "hidden";
    hiddenInput.name = name;
    hiddenInput.value = value;
    this.element.appendChild(hiddenInput);
  }

  isImage(file) {
    return file.type.startsWith("image/");
  }

  formatFileName(filename) {
    if (filename.length > 20) {
      return `${filename.substring(0, 10)}...${filename.slice(-7)}`;
    }
    return filename;
  }
}
